import { Card, Grid } from "@mui/material";

import Helpers from "../../commons/helpers";
import Autocomplete from "../../components/Autocomplete";
import Box from "../../components/Box";
import FormField from "../../components/FormField";
import Typography from "../../components/Typography";
import useStrings from "../../hooks/useStrings";

import { Add, CheckBoxOutlined, CropSquareOutlined, ExpandLess, ExpandMore } from "@mui/icons-material";
import { useMemo, useState } from "react";
import { ICodename, IModuleResourceResponse, IMultiLanguageContent, IPropDetailContainer, IResource } from "../../commons/interfaces";
import Button from "../../components/Button";
import ControlLabelCheckBox from "../../components/ControlLabelCheckBox";
import HeaderButton from "../../components/HeaderButton";
import { SelectMultiLanguage } from "../../components/MultiLanguage";
import { ModalAddResourceController } from "../../components/Resource";
import ResourcePermissionTable from "../../components/Resource/Table/ResourcePermissionTable";
import { AdminFeature, Mode } from "../../constants/enums";
import { IResourceError, useData, useSelectData, useSubmit } from "../../hooks/resource/edit";
import { useCommonComponentContext } from "../../providers/CommonComponentProvider";
import { MainComponent } from "../main";
import { RoleHelpers } from "../../commons/roleHelpers";

const ResourceEditContainer = ({ idDetail, onGoBack, onChangeMode, mode }: IPropDetailContainer) => {
    const Strings = useStrings();
    const { userInfo, language: defaultLanguage } = useCommonComponentContext();
    const [language, setLanguage] = useState<string>(defaultLanguage);
    const { resources, services, getAllResource, getAllService, accessModes } = useSelectData();

    const { acUpdate, data, error, isEdited, onChangeValue, roleTableMode, setRoleTableMode, visibled, setVisibled, setError, onAddResource } =
        useData({
            getAllResource,
            getAllService,
            idDetail,
        });
    const { onSubmit } = useSubmit({ data, mode, onChangeMode, setError, onGoBack, setLanguage });

    const resourceData = useMemo(
        () =>
            (data?.moduleResourceResponses || []).map(
                (el) =>
                    ({
                        resourceCode: el.resourceCode,
                    } as IResource)
            ),
        [data?.moduleResourceResponses]
    );

    return (
        <MainComponent>
            <HeaderButton
                hidenActionCreate={acUpdate}
                hidenActionUpdate={!acUpdate}
                isEdited={isEdited}
                mode={mode}
                onGoBack={onGoBack}
                onSubmit={onSubmit}
            />

            <Card>
                <Box p={3}>
                    <Box alignItems="center" justifyContent="space-between" sx={{ display: "flex", flexWrap: "wrap" }}>
                        <Typography variant="h5">{Strings.RESOURCE.BASIC_INFO}</Typography>
                        {mode !== Mode.View && (
                            <SelectMultiLanguage
                                defaultLanguage={language}
                                onChangeLanguage={(value) => {
                                    setLanguage(value);
                                }}
                            />
                        )}
                    </Box>

                    <ResourceBasicInfo {...{ accessModes, data, error, language, mode, onChangeValue, services }} />

                    <Box my={1}>
                        {mode !== Mode.View && (
                            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                                <Button
                                    color={"dark"}
                                    variant="outlined"
                                    onClick={() => setRoleTableMode((prev) => (prev === "normal" ? "advance" : "normal"))}
                                >
                                    {roleTableMode === "normal" ? <ExpandMore /> : <ExpandLess />}
                                    &nbsp;
                                    {roleTableMode !== "normal" ? Strings.Common.NORMAL : Strings.Common.ADVANCE}
                                </Button>
                                <Button color={"dark"} onClick={() => setVisibled(true)}>
                                    <Add />
                                    &nbsp;
                                    {Strings.Common.ADD_RESOURCES}
                                </Button>
                            </Box>
                        )}
                    </Box>
                    <ResourcePermissionTable
                        tableMode={roleTableMode}
                        // key={JSON.stringify(data?.moduleResourceResponses)}
                        disabled={mode === Mode.View}
                        resources={resources || []}
                        resourceModules={data?.moduleResourceResponses || []}
                        onChangePermission={onChangeValue("moduleResourceResponses")}
                        // roleResourcePermissions={data?.roleResourcePermissions || []}
                        // onChangePermission={(value) => onChangeValue("roleResourcePermissions")(value)}
                    />

                    <ModalAddResourceController
                        key={JSON.stringify(resourceData)}
                        resources={resources || []}
                        resourceData={resourceData}
                        visibled={visibled}
                        serviceCode={data.serviceCode}
                        onClose={() => setVisibled(false)}
                        onSubmit={onAddResource}
                    />
                </Box>
            </Card>
        </MainComponent>
    );
};

const ResourceBasicInfo = ({
    mode,
    data,
    error,
    services,
    accessModes,
    language,
    onChangeValue,
}: {
    mode: Mode;
    data: IResource;
    services: ICodename[];
    accessModes: ICodename[];
    language: string;
    error: IResourceError;
    onChangeValue: (key: keyof IResource) => (value: string | number | IMultiLanguageContent | IModuleResourceResponse[]) => void;
}) => {
    const Strings = useStrings();
    const { userInfo } = useCommonComponentContext();

    return (
        <>
            <Grid container spacing={3} p={2} alignContent="center">
                <Grid item xs={12} md={6}>
                    <FormField
                        required
                        maxLength={255}
                        mode={mode}
                        label={Strings.RESOURCE.GROUP_NAME}
                        placeholder={Strings.RESOURCE.INPUT_GROUP_NAME}
                        errorMessage={error?.resourceName}
                        onBlur={(value) =>
                            onChangeValue("resourceName")({
                                ...data?.resourceName,
                                value: {
                                    ...data.resourceName?.value,
                                    [language]: value,
                                },
                            })
                        }
                        defaultValue={Helpers.getDefaultValueMultiLanguage(data?.resourceName, language)}
                    />
                </Grid>
                {!RoleHelpers.isOrganizationUser(userInfo?.userProfile) && (
                    <Grid item xs={12} md={6}>
                        <Autocomplete
                            isSelectedBox
                            mode={mode}
                            data={services || []}
                            label={Strings.RESOURCE.SERVICE}
                            placeholder={Strings.RESOURCE.SELECT_SERVICE}
                            defaultValue={data?.serviceCode}
                            errorMessage={error?.serviceCode}
                            disabled={!Helpers.isNullOrEmpty(data?.id)}
                            onChange={onChangeValue("serviceCode")}
                        />
                    </Grid>
                )}

                <Grid item xs={12} md={6}>
                    <Autocomplete
                        isSelectedBox
                        mode={mode}
                        data={accessModes || []}
                        label={Strings.RESOURCE.ROLE_ACCESS}
                        placeholder={Strings.RESOURCE.SELECT_ROLE_ACCESS}
                        defaultValue={data?.accessMode}
                        errorMessage={error?.accessMode}
                        onChange={onChangeValue("accessMode")}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <FormField
                        maxLength={255}
                        mode={mode}
                        label={Strings.RESOURCE.RESOURCE_URI}
                        placeholder={Strings.RESOURCE.INPUT_RESOURCE_URI}
                        defaultValue={data?.resourceURI}
                        errorMessage={error?.resourceURI}
                        onBlur={onChangeValue("resourceURI")}
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormField
                        multiline
                        isFullRow
                        maxLength={500}
                        mode={mode}
                        label={Strings.RESOURCE.DESCRIPTION}
                        placeholder={Strings.RESOURCE.INPUT_DESCRIPTION}
                        errorMessage={error.description}
                        onBlur={(value) =>
                            onChangeValue("description")({
                                ...data?.description,
                                value: {
                                    ...data.description?.value,
                                    [language]: value,
                                },
                            })
                        }
                        defaultValue={Helpers.getDefaultValueMultiLanguage(data?.description, language)}
                    />
                </Grid>
                <Grid item xs={12} display="flex" alignItems="end">
                    {mode === Mode.View ? (
                        <Box display="flex" alignItems="center">
                            {data?.adminFeature === AdminFeature.AdminFeature ? <CheckBoxOutlined /> : <CropSquareOutlined />}
                            &ensp;
                            <Typography fontWeight="bold" variant="button">
                                {Strings.RESOURCE.ADMIN_FEATURE}
                            </Typography>
                        </Box>
                    ) : (
                        <ControlLabelCheckBox
                            label={Strings.RESOURCE.ADMIN_FEATURE}
                            value={data?.adminFeature === AdminFeature.AdminFeature}
                            onChangeValue={() =>
                                onChangeValue("adminFeature")(
                                    data?.adminFeature === AdminFeature.AdminFeature ? AdminFeature.None : AdminFeature.AdminFeature
                                )
                            }
                        />
                    )}
                </Grid>
            </Grid>
        </>
    );
};

export default ResourceEditContainer;
